import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ROUTE_CHAT,
  ROUTE_COMMANDS,
  ROUTE_DEMO,
  ROUTE_LOGIN,
  isDocsRoute,
  routeByHref,
  screenTitle,
} from '@app/navigation';
import { t } from '@app/i18n';
import { useLocale } from '@app/i18n/useLocale';

/**
 * Título da aba do navegador por rota (30/08, pedido do mrolim): `360 AI - {tela}`.
 *
 * Era fixo no `index.html` (`360 Agentic Identity`), então quinze telas abertas
 * em quinze abas ficavam indistinguíveis. O nome de cada tela **não é inventado
 * aqui**: sai do `titulo` que `ROTAS` já declara — o mesmo que vira o `<h1>`.
 *
 * O desenho (guardar o anterior, trocar, restaurar no cleanup) é o de
 * `ApiDocsPage`, que já fazia isso desde 30/08.
 */

/** Como o mrolim pediu: hífen simples entre espaços, não travessão. */
const PREFIX = '360 AI - ';

/**
 * As rotas que vivem **fora de `ROTAS`** e ainda assim merecem título.
 *
 * `/comandos` e `/console-de-demo` estão fora da lista porque são ocultas (o
 * rail e a busca saem de `ROTAS`) — mas ocultas do menu, não da aba: quem chega
 * a elas pela URL merece saber em qual está, e é justamente quem tem várias
 * abas abertas na hora da demonstração. O nome é o que cada tela mostra no
 * próprio cabeçalho.
 *
 * `/login` ganha «Entrar», e não a marca: a marca já é o prefixo, e repeti-la
 * daria «360 AI - 360 Agentic Identity».
 *
 * **`/docs/api` não está aqui de propósito** — ver `semTitulo` abaixo.
 */
const OUTSIDE_ROUTES: Readonly<Record<string, string>> = {
  [ROUTE_LOGIN]: 'route.login',
  [ROUTE_COMMANDS]: 'route.commands',
  [ROUTE_DEMO]: 'route.demo',
  [ROUTE_CHAT]: 'route.chat',
};

/**
 * Rotas que definem o próprio `document.title` e por isso este hook **não toca**.
 *
 * São as de documentação — `/docs/api` e `/docs/<trilha>/<pagina>`: cada uma guarda o
 * anterior, escreve o seu e restaura ao sair. Sem esta saída os dois escreveriam no mesmo
 * lugar no mesmo commit — e como os efeitos do filho rodam antes dos do pai, quem
 * escreveria por último seria este hook, apagando o título da própria página que o
 * definiu.
 *
 * O motivo de `/docs` não poder ser servida daqui é de dado, não de estilo: o nome da
 * página vem do sumário, que a tela busca por HTTP — este hook só conhece o `pathname`.
 */
function withoutTitle(pathname: string): boolean {
  return isDocsRoute(pathname);
}

/** Nome do produto na documentação — o da tela, não o do manual (protótipo de 16/09, §3). */
export const DOCS_PRODUCT = '360 Agentic Identity';

/**
 * Título da aba da documentação do produto: `360 Agentic Identity — Documentação — <página>`.
 *
 * Fica neste arquivo, e não na tela, porque a convenção de título é uma só e já morava
 * aqui — o travessão é o separador da documentação, ao contrário do hífen simples do
 * console (`PREFIX` acima), que o mrolim pediu para as telas.
 */
export function docsTabTitle(section: string, page?: string): string {
  const head = `${DOCS_PRODUCT} — ${section}`;
  return page ? `${head} — ${page}` : head;
}

/**
 * Escreve `document.title` enquanto o componente estiver montado e restaura o anterior na
 * saída — o desenho que `ApiDocsPage` já usava desde 30/08, agora compartilhado.
 *
 * `undefined` deixa o título como está: é o quadro em que a página ainda não sabe o nome
 * do que está mostrando, e piscar para a marca e voltar é pior que esperar.
 */
export function useDocumentTitle(title: string | undefined): void {
  useEffect(() => {
    if (!title) return;
    const previous = document.title;
    document.title = title;
    return () => {
      document.title = previous;
    };
  }, [title]);
}

/**
 * Chamado uma vez, no topo das rotas.
 *
 * **URL desconhecida não precisa de tratamento** e é por isso que não há um
 * "padrão" aqui: `path="*"` manda para a Visão geral com `replace`, então o
 * `pathname` que este hook vê já é `/`. O título acompanha a tela que está na
 * frente da pessoa, não o endereço que ela digitou — que é o que o roteiro da
 * apresentação pede. No render de passagem, antes do redirecionamento, não há
 * título conhecido e o hook deixa o que estava: melhor um título velho por um
 * quadro que um piscar para a marca e de volta.
 */
export function useTabTitle(): void {
  const { pathname } = useLocation();
  const locale = useLocale();

  useEffect(() => {
    if (withoutTitle(pathname)) return;

    const rota = routeByHref(pathname);
    // Fora de `ROTAS`, o mapa guarda a CHAVE do catálogo, não a frase — mesma regra
    // do menu: quem resolve é o efeito, a cada navegação, para o seletor de idioma
    // valer também na aba do navegador.
    const outside = OUTSIDE_ROUTES[pathname];
    const screen = rota ? screenTitle(rota) : outside ? t(`shell.${outside}`) : undefined;
    if (!screen) return;

    const previous = document.title;
    document.title = `${PREFIX}${screen}`;
    return () => {
      document.title = previous;
    };
    // `locale` na dependência: o título lê o catálogo, e o seletor de idioma tem de
    // valer na aba do navegador sem esperar uma navegação — sem isto a aba ficava
    // «360 AI - Aprovações» com a tela já em inglês (rodada 2 do spec, 09/09).
  }, [pathname, locale]);
}
